// Unified data-source provenance vocabulary (SOURCE_CITATION_AND_PREFS_PLAN.md).
//
// Several features blend a *trusted external source* (NLM RxNav, the FDA drug
// label via openFDA/DailyMed, our hand-verified curated tables) with an *AI
// estimate* from a Bedrock model. So users can weigh the information, we cite
// where each datum came from with a consistent label, a trust class (for styling
// trusted vs. AI vs. user-provided differently), and — where a stable per-drug
// page exists — an outbound link.
//
// This consolidates the previously-scattered label maps (DoseLimitsSource,
// FoodGuidanceSource, the interaction-modal footer text) into one place. The
// per-feature source unions are all subsets of DataSourceKind, so values from
// those features can be passed straight through.

/** Every provenance value used across the app, in rough trust order. */
export type DataSourceKind =
  | "curated"
  | "openfda"
  | "dailymed"
  | "rxnav"
  | "llm"
  | "user";

/** How a source should be presented — drives the badge colour. */
export type DataSourceTrust = "trusted" | "ai" | "user";

export interface DataSourceMeta {
  label: string;
  trust: DataSourceTrust;
  /**
   * Plain-English, one-sentence explanation of where the data comes from and how
   * far to trust it. Written for end users (no jargon) and surfaced in the source
   * legend (see SourceLegend).
   */
  description: string;
  /**
   * Builds a stable outbound URL to a human-readable page for `query` (a drug
   * name), or is absent when the source has no per-item page (curated tables,
   * AI estimates, the user's own instructions).
   */
  url?: (query: string) => string;
}

const enc = (q: string) => encodeURIComponent(q.trim());

// DailyMed hosts the official FDA-approved labels that openFDA serves as data,
// so both the "FDA drug label" and "DailyMed" sources link to a DailyMed search.
const dailyMedSearch = (q: string) =>
  `https://dailymed.nlm.nih.gov/dailymed/search.cfm?query=${enc(q)}`;

export const DATA_SOURCE_META: Record<DataSourceKind, DataSourceMeta> = {
  curated: {
    label: "Standard reference",
    trust: "trusted",
    description:
      "A fixed, human-verified value from established pharmacology references, built into the app — the most reliable source shown here.",
  },
  openfda: {
    label: "FDA drug label",
    trust: "trusted",
    description: "Summarized from the official FDA-approved drug label.",
    url: dailyMedSearch,
  },
  dailymed: {
    label: "DailyMed",
    trust: "trusted",
    description: "From the official FDA-approved drug label, published on the NIH's DailyMed.",
    url: dailyMedSearch,
  },
  rxnav: {
    label: "NLM RxNav",
    trust: "trusted",
    description: "From the U.S. National Library of Medicine's RxNav drug database.",
    url: (q) => `https://mor.nlm.nih.gov/RxNav/search?searchBy=String&searchTerm=${enc(q)}`,
  },
  llm: {
    label: "AI estimate",
    trust: "ai",
    description:
      "Our AI model's best estimate, used when the drug isn't in the built-in references. Treat as approximate.",
  },
  user: {
    label: "your instructions",
    trust: "user",
    description: "Based on the instructions you entered yourself.",
  },
};

/** Short, user-facing label for a source (no link). */
export function dataSourceLabel(source: DataSourceKind): string {
  return DATA_SOURCE_META[source].label;
}

/** Outbound URL for a source + drug name, or undefined when none applies. */
export function dataSourceUrl(source: DataSourceKind, query?: string): string | undefined {
  const meta = DATA_SOURCE_META[source];
  if (!meta.url || !query || !query.trim()) return undefined;
  return meta.url(query);
}
